import { forwardRef, type AnchorHTMLAttributes, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { Button, type ButtonProps } from "./Button";
import { ArrowUpRightIcon } from "@phosphor-icons/react/dist/csr/ArrowUpRight";

export interface ButtonGroupProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
}

/** Compact action group on a soft V2 track; destructive actions stay outside. */
export function ButtonGroup({ label, className, children, ...props }: ButtonGroupProps) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn(
        "studio-button-group inline-flex max-w-full items-stretch overflow-x-auto bg-canvas-soft [&>*]:shrink-0",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export const textLinkVariants = "rounded-sm font-medium text-ink no-underline hover:text-accent-text focus-visible:text-accent-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)]";

/** Quiet text action with an expanded touch target, for in-place operations. */
export const InlineAction = forwardRef<HTMLButtonElement, Omit<ButtonProps, "variant" | "size">>(
  function InlineAction({ className, ...props }, ref) {
    return <Button {...props} ref={ref} variant="ghost" size="sm" className={cn("studio-inline-action", className)} />;
  },
);

export interface TextLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  external?: boolean;
  unavailable?: boolean;
  unavailableReason?: string;
}

/** Quiet ink link: weight carries the resting affordance, brand color appears on hover. */
export const TextLink = forwardRef<HTMLAnchorElement, TextLinkProps>(function TextLink(
  { className, children, external, unavailable, unavailableReason, target, rel, ...props },
  ref,
) {
  if (unavailable) {
    return (
      <span
        title={unavailableReason}
        className={cn(
          "cursor-not-allowed text-ink-muted",
          className,
        )}
      >
        {children}
      </span>
    );
  }
  return (
    <a
      ref={ref}
      target={external ? "_blank" : target}
      rel={external ? "noreferrer" : rel}
      className={cn(
        textLinkVariants,
        className,
      )}
      {...props}
    >
      {children}
      {external ? <ArrowUpRightIcon aria-hidden="true" weight="bold" className="ms-1 inline size-3" /> : null}
    </a>
  );
});

export interface UnavailableTextProps {
  children: ReactNode;
  reason: string;
}

export function UnavailableText({ children, reason }: UnavailableTextProps) {
  return <span title={reason} className="text-ink-muted">{children}</span>;
}
